import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Search, 
  MapPin, 
  Smartphone,
  Building2, 
  Phone, 
  Globe, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { boPath } from '@/utils/getBasePath';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Wizard Steps (the indicator shows exactly these). The phone is verified
// with a texted code LAST: the restaurant is created only once the code is
// right (owner, Oct 27 2026), and the server refuses it otherwise.
const STEPS = {
  SEARCH: 'search',
  CONNECT: 'connect',
  CONFIRM: 'confirm',
  VERIFY_PHONE: 'verify_phone'
};

export default function OnboardingContinue() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signupId = searchParams.get('signup_id');
  const googleStatus = searchParams.get('google');
  const errorParam = searchParams.get('error');
  const googleEmail = searchParams.get('google_email');
  
  // State
  const [currentStep, setCurrentStep] = useState(STEPS.SEARCH);
  const [loading, setLoading] = useState(false);
  const [signupInfo, setSignupInfo] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [searching, setSearching] = useState(false);
  // Business search not set up on the server (503): say so once, offer manual entry
  const [searchUnavailable, setSearchUnavailable] = useState('');
  
  // GBP state
  const [gbpLocations, setGbpLocations] = useState([]);
  const [loadingGbp, setLoadingGbp] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    restaurant_name: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    website: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Phone verification state
  const [codeSentTo, setCodeSentTo] = useState('');   // the number the code was texted to
  const [code, setCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [resendIn, setResendIn] = useState(0);
  // the number the code goes to: filled from sign-up, the owner can correct it
  const [phoneInput, setPhoneInput] = useState('');
  
  // Load signup info on mount
  const loadSignup = useLatestCallback(() => {
    if (!signupId) {
      toast.error('Invalid signup link');
      navigate('/owner/signup');
      return;
    }
    
    loadSignupInfo();
    loadOAuthStatus();
  });
  useEffect(() => {
    loadSignup();
  }, [signupId, loadSignup]);
  
  const loadSignupInfo = async () => {
    try {
      const res = await fetch(`${API}/api/public/onboarding/restaurant/owner/signup/${signupId}`);
      if (res.ok) {
        const data = await res.json();
        setSignupInfo(data);
      } else {
        toast.error('Signup not found');
        navigate('/owner/signup');
      }
    } catch (err) {
      console.error('Failed to load signup:', err);
    }
  };
  
  const loadOAuthStatus = useLatestCallback(async () => {
    if (!signupId) return;
    try {
      const res = await fetch(`${API}/api/onboarding/google/oauth/status?signup_id=${signupId}`);
      if (res.ok) {
        const data = await res.json();
        setOauthStatus(data);
        
        // If connected, load autofill and move to confirm
        if (data.status === 'connected') {
          await loadAutofill();
          setCurrentStep(STEPS.CONFIRM);
        }
      }
    } catch (err) {
      console.error('Failed to load OAuth status:', err);
    }
  });

  // Handle OAuth callback
  useEffect(() => {
    if (googleStatus === 'connected') {
      toast.success('Google account connected successfully!');
      loadOAuthStatus();
      setCurrentStep(STEPS.CONFIRM);
    }
    
    if (errorParam === 'email_mismatch') {
      toast.error(`Please sign in with the same email as your signup (not ${googleEmail})`);
    } else if (errorParam) {
      toast.error(`Google connection failed: ${errorParam.replace(/_/g, ' ')}`);
    }
  }, [googleStatus, errorParam, googleEmail, loadOAuthStatus]);
  
  const loadAutofill = async () => {
    try {
      const res = await fetch(`${API}/api/onboarding/google/autofill?signup_id=${signupId}`);
      if (res.ok) {
        const data = await res.json();
        setFormData({
          restaurant_name: data.restaurant_name || '',
          phone: data.phone || '',
          address_line1: data.address_line1 || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          country: data.country || 'US',
          website: data.website || ''
        });
      }
    } catch (err) {
      console.error('Failed to load autofill:', err);
    }
  };
  
  // ---- phone verification ----
  const errorText = (body, fallback) =>
    (body && typeof body.detail === 'object' ? body.detail.message : body?.detail) || fallback;

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // (+16195550100 -> (619) 555-0100) for the phone field
  const formatPhone = (e164) => {
    const d = (e164 || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (e164 || '');
  };

  useEffect(() => {
    if (signupInfo?.phone_e164 && !phoneInput) setPhoneInput(formatPhone(signupInfo.phone_e164));
  }, [signupInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCode = async () => {
    setSendingCode(true);
    setCodeError('');
    try {
      const res = await fetch(`${API}/api/public/onboarding/restaurant/owner/phone/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // the number on screen (the server keeps it as the signup's phone)
        body: JSON.stringify({ signup_id: signupId, phone: phoneInput })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        if (body.verified) {
          setSignupInfo(prev => ({ ...prev, phone_verified: true }));
          await handleSubmit();
          return;
        }
        setCodeSentTo(phoneInput);
        setResendIn(body.resend_after || 60);
        setCode('');
        loadSignupInfo();
      } else if (res.status === 429 && body.detail?.retry_after) {
        setResendIn(body.detail.retry_after);
        setCodeError(`Please wait ${body.detail.retry_after} seconds before asking for another code.`);
      } else if (res.status >= 500 && !body.detail) {
        setCodeError('Our text service did not answer. Please try again in a moment.');
      } else {
        setCodeError(errorText(body, 'Could not send a code. Please try again.'));
      }
    } catch (err) {
      setCodeError('Could not send a code. Check your connection and try again.');
    } finally {
      setSendingCode(false);
    }
  };

  // Android Chrome: read the code straight from the text message (WebOTP); the
  // text ends "@chain29.com #123456". iPhone offers it above the keyboard
  // (autocomplete="one-time-code"). Either way the 6th digit verifies.
  useEffect(() => {
    if (!codeSentTo || !('OTPCredential' in window)) return undefined;
    const ac = new AbortController();
    navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then(otp => { if (otp?.code) { setCode(otp.code); verifyCode(otp.code); } })
      .catch(() => {});
    return () => ac.abort();
  }, [codeSentTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyCode = async (value = code) => {
    if (verifyingCode || submitting || (value || '').length !== 6) return;
    setVerifyingCode(true);
    setCodeError('');
    try {
      const res = await fetch(`${API}/api/public/onboarding/restaurant/owner/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signup_id: signupId, code: value.trim() })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.verified) {
        toast.success('Phone verified');
        setSignupInfo(prev => ({ ...prev, phone_verified: true }));
        await handleSubmit();
      } else {
        setCodeError(errorText(body, 'That code did not work. Please try again.'));
        setCode('');
      }
    } catch (err) {
      setCodeError('Could not check the code. Check your connection and try again.');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Live search with debounce
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const handleSearch = async (query = searchQuery) => {
    if (searchUnavailable) return;
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const params = new URLSearchParams({
        signup_id: signupId,
        q: query
      });
      if (searchCity) params.append('city', searchCity);
      
      const res = await fetch(`${API}/api/onboarding/google/places/search?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
        if (data.results?.length === 0) {
          toast.info('No businesses found. Try a different search.');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 503) {
          setSearchUnavailable(err.detail || 'Business search is not available right now.');
          setSearchResults([]);
        } else {
          toast.error(err.detail ? `${err.detail}. You can enter your details manually below.`
                                 : "Business search isn't responding. You can enter your details manually below.");
        }
      }
    } catch (err) {
      toast.error("Business search isn't responding. You can enter your details manually below.");
    } finally {
      setSearching(false);
    }
  };
  
  // Handle live search input with debounce
  const handleSearchInput = (value) => {
    setSearchQuery(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for live search
    if (!searchUnavailable && value.trim().length >= 2) {
      const timeout = setTimeout(() => {
        handleSearch(value);
      }, 500); // 500ms debounce
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
    }
  };
  
  // The picked business's address split into fields (street, city, state,
  // ZIP) from Google's place details; the search result alone has one line.
  const fillFromPlace = (place, details) => {
    setFormData(prev => ({
      ...prev,
      restaurant_name: details?.name || place.name,
      phone: details?.phone || place.phone || '',
      address_line1: details?.address_line1 || place.formatted_address?.split(',')[0] || '',
      city: details?.city || '',
      state: details?.state || '',
      zip: details?.zip || '',
      country: details?.country || prev.country,
      website: details?.website || place.website || ''
    }));
  };

  const handleSelectPlace = async (place) => {
    setSelectedPlace(place);
    fillFromPlace(place, null);
    try {
      const params = new URLSearchParams({ signup_id: signupId, place_id: place.place_id });
      const res = await fetch(`${API}/api/onboarding/google/places/details?${params}`);
      if (res.ok) {
        const details = await res.json();
        setSelectedPlace({ ...place, details });
        fillFromPlace(place, details);
      }
    } catch (err) {
      // the search result's own fields stay; the owner can edit them
    }
    
    // If already connected to Google, move to confirm
    if (oauthStatus?.status === 'connected') {
      setCurrentStep(STEPS.CONFIRM);
    } else {
      setCurrentStep(STEPS.CONNECT);
    }
  };
  
  const handleConnectGoogle = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        signup_id: signupId
      });
      if (selectedPlace) {
        params.append('place_id', selectedPlace.place_id);
        params.append('place_name', selectedPlace.name);
        params.append('place_address', selectedPlace.formatted_address);
      }
      
      const res = await fetch(`${API}/api/onboarding/google/oauth/start?${params}`);
      if (res.ok) {
        const data = await res.json();
        // Redirect to Google OAuth
        window.location.href = data.redirect_url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start Google connection');
      }
    } catch (err) {
      toast.error('Failed to connect Google');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSkipGoogle = () => {
    // Continue without connecting Google: the picked business stays filled in
    if (selectedPlace) {
      fillFromPlace(selectedPlace, selectedPlace.details);
    }
    setCurrentStep(STEPS.CONFIRM);
  };
  
  const handleLoadGbpLocations = async () => {
    setLoadingGbp(true);
    try {
      const res = await fetch(`${API}/api/onboarding/google/business/locations?signup_id=${signupId}`);
      if (res.ok) {
        const data = await res.json();
        setGbpLocations(data.locations || []);
        
        // If there's a match, auto-select it
        if (data.matched_location) {
          const matched = data.locations.find(l => l.location_name === data.matched_location);
          if (matched) {
            await handleSelectGbpLocation(matched);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load GBP locations:', err);
    } finally {
      setLoadingGbp(false);
    }
  };
  
  const handleSelectGbpLocation = async (location) => {
    try {
      await fetch(`${API}/api/onboarding/google/business/select-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signup_id: signupId,
          location_name: location.location_name,
          account_name: location.account_name
        })
      });
      
      // Update form with GBP data
      setFormData(prev => ({
        ...prev,
        restaurant_name: location.title || prev.restaurant_name,
        phone: location.phone || prev.phone,
        address_line1: location.address || prev.address_line1,
        city: location.city || prev.city,
        state: location.state || prev.state,
        zip: location.zip || prev.zip,
        website: location.website || prev.website
      }));
      
      toast.success('Business Profile data imported!');
    } catch (err) {
      console.error('Failed to select GBP location:', err);
    }
  };
  
  const handleSubmit = async () => {
    if (!formData.restaurant_name.trim()) {
      toast.error('Restaurant name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/onboarding/restaurant/owner/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signup_id: signupId,
          ...formData,
          // the Google business picked in step 1 is saved on the restaurant
          google_place_id: selectedPlace?.place_id || null
        })
      });
      
      const data = await res.json();
      
      if (data.ok) {
        // Signed in the way owner sign-in does it: the back office reads
        // backoffice_token and the active restaurant
        if (data.access_token) {
          localStorage.setItem('backoffice_token', data.access_token);
        }
        if (data.restaurant_id) {
          localStorage.setItem('backoffice_active_restaurant', String(data.restaurant_id));
        }
        window.dispatchEvent(new Event('backoffice-login'));
        
        toast.success('Welcome! Your restaurant has been created.');
        
        // signed in; sign-up finishes with a plan and a billing card (owner, Nov 5 2026)
        navigate('/owner/signup/billing');
      } else if (data.code === 'PHONE_NOT_VERIFIED') {
        toast.error(data.message);
        setSignupInfo(prev => ({ ...prev, phone_verified: false }));
        setCodeSentTo('');
        setCurrentStep(STEPS.VERIFY_PHONE);
      } else {
        toast.error(data.message || 'Failed to complete onboarding');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Object.values(STEPS).map((step, idx) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
            currentStep === step 
              ? 'bg-primary text-primary-foreground' 
              : Object.values(STEPS).indexOf(currentStep) > idx
                ? 'bg-green-500 text-white'
                : 'bg-muted text-muted-foreground'
          }`}>
            {Object.values(STEPS).indexOf(currentStep) > idx ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              idx + 1
            )}
          </div>
          {idx < Object.values(STEPS).length - 1 && (
            <div className={`w-12 h-0.5 mx-1 ${
              Object.values(STEPS).indexOf(currentStep) > idx 
                ? 'bg-green-500' 
                : 'bg-muted'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
  
  // Render phone verification step
  const renderVerifyPhoneStep = () => (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Smartphone className="w-4 h-4 sm:w-5 sm:h-5" />
          Verify Your Phone
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Last step: we text a 6-digit code to your cell phone. Your restaurant is created once it is verified.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
        {!codeSentTo ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="verify-phone">Cell phone</Label>
              <Input
                id="verify-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="h-11 sm:h-10 text-base"
                data-testid="verify-phone-input"
              />
              <p className="text-xs text-muted-foreground">Check the number - the code is texted here.</p>
            </div>
            {codeError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs sm:text-sm">{codeError}</AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full min-h-[48px] sm:min-h-[40px]"
              onClick={sendCode}
              disabled={sendingCode || resendIn > 0 || phoneInput.replace(/\D/g, '').length < 10}
              data-testid="send-code-btn"
            >
              {sendingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
              {resendIn > 0 ? `Text me a code (in ${resendIn}s)` : 'Text me a code'}
            </Button>
          </>
        ) : (
          <>
            <div className="p-3 bg-muted/50 rounded-lg text-sm flex items-center justify-between gap-2">
              <span>Code sent to <strong>{codeSentTo}</strong></span>
              <Button variant="link" className="p-0 h-auto" onClick={() => { setCodeSentTo(''); setCode(''); setCodeError(''); }}
                      data-testid="change-number-btn">
                Change number
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone-code">Verification code</Label>
              {/* six boxes; typing, paste and the phone's code suggestion all fill them,
                  and the 6th digit verifies */}
              <div className="flex justify-center">
                <InputOTP
                  id="phone-code"
                  maxLength={6}
                  value={code}
                  onChange={(v) => setCode(v.replace(/\D/g, ''))}
                  onComplete={(v) => verifyCode(v)}
                  inputMode="numeric"
                  pattern="^[0-9]*$"
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={verifyingCode || submitting}
                  data-testid="phone-code-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map(i => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-11 sm:h-12 sm:w-12 text-xl font-semibold" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            {codeError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs sm:text-sm">{codeError}</AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full min-h-[48px] sm:min-h-[40px]"
              onClick={() => verifyCode()}
              disabled={verifyingCode || submitting || code.length !== 6}
              data-testid="verify-phone-btn"
            >
              {verifyingCode || submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify &amp; Create Restaurant
            </Button>
            <Button variant="ghost" className="w-full" onClick={sendCode} disabled={sendingCode || resendIn > 0}
                    data-testid="resend-code-btn">
              {sendingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
            </Button>
          </>
        )}
        <Button variant="ghost" className="w-full" onClick={() => setCurrentStep(STEPS.CONFIRM)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to your details
        </Button>
      </CardContent>
    </Card>
  );

  // Render search step
  const renderSearchStep = () => (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Search className="w-4 h-4 sm:w-5 sm:h-5" />
          Find Your Restaurant
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Search for your business to auto-fill your information
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <Input
              placeholder="Restaurant name... (type to search)"
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="h-11 sm:h-10 text-base"
              data-testid="business-search-input"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="City"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              className="w-full sm:w-32 h-11 sm:h-10 text-base"
            />
            <Button onClick={() => handleSearch()} disabled={searching || !!searchUnavailable} className="min-h-[44px] min-w-[44px]">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {searchUnavailable && (
          <Alert data-testid="business-search-unavailable">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs sm:text-sm">
              {searchUnavailable} You can enter your restaurant's details yourself below.
            </AlertDescription>
          </Alert>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-2 max-h-60 sm:max-h-80 overflow-y-auto">
            {searchResults.map((place) => (
              <Card 
                key={place.place_id}
                className={`cursor-pointer hover:border-primary transition-colors ${
                  selectedPlace?.place_id === place.place_id ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => handleSelectPlace(place)}
                data-testid={`place-result-${place.place_id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-medium text-sm sm:text-base truncate">{place.name}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{place.formatted_address}</span>
                      </p>
                      {place.phone && (
                        <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          {place.phone}
                        </p>
                      )}
                    </div>
                    {selectedPlace?.place_id === place.place_id && (
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Skip option */}
        <div className="pt-4 border-t">
          <Button
            variant="ghost"
            className="w-full min-h-[44px] text-sm sm:text-base"
            onClick={() => setCurrentStep(STEPS.CONFIRM)}
          >
            Can't find your business? Enter details manually
          </Button>
        </div>
      </CardContent>
    </Card>
  );
  
  // Render connect step
  const renderConnectStep = () => (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
          Connect Google Business Profile
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Link your Google account to import your business information
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-6">
        {selectedPlace && (
          <div className="p-3 sm:p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm sm:text-base">{selectedPlace.name}</h4>
            <p className="text-xs sm:text-sm text-muted-foreground">{selectedPlace.formatted_address}</p>
          </div>
        )}
        
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs sm:text-sm">
            <strong>Important:</strong> Sign in with the same Google email you used to sign up ({signupInfo?.email}).
          </AlertDescription>
        </Alert>
        
        <Button 
          className="w-full min-h-[48px]" 
          size="lg"
          onClick={handleConnectGoogle}
          disabled={loading}
          data-testid="connect-google-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Connect Google Account
        </Button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or</span>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full"
          onClick={handleSkipGoogle}
        >
          Skip and enter details manually
        </Button>
        
        <Button 
          variant="ghost" 
          className="w-full"
          onClick={() => setCurrentStep(STEPS.SEARCH)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to search
        </Button>
      </CardContent>
    </Card>
  );
  
  // Render confirm step
  const renderConfirmStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Confirm Your Information
        </CardTitle>
        <CardDescription>
          Review and edit your restaurant details
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* GBP Locations (if connected) */}
        {oauthStatus?.status === 'connected' && oauthStatus?.has_gbp_access && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Google connected as {oauthStatus.google_email}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLoadGbpLocations}
                disabled={loadingGbp}
              >
                {loadingGbp ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Load Business Profiles
              </Button>
            </div>
            
            {gbpLocations.length > 0 && (
              <div className="space-y-2 mt-3">
                <p className="text-sm text-muted-foreground">Select a business profile to import:</p>
                {gbpLocations.map((loc) => (
                  <Button
                    key={loc.location_name}
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => handleSelectGbpLocation(loc)}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {loc.title} - {loc.address}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Form Fields */}
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Restaurant Name *</Label>
            <Input
              value={formData.restaurant_name}
              onChange={(e) => setFormData(prev => ({ ...prev, restaurant_name: e.target.value }))}
              placeholder="Your Restaurant Name"
              data-testid="restaurant-name-input"
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://yourrestaurant.com"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={formData.address_line1}
              onChange={(e) => setFormData(prev => ({ ...prev, address_line1: e.target.value }))}
              placeholder="123 Main St"
            />
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="San Francisco"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                placeholder="CA"
              />
            </div>
            <div className="space-y-2">
              <Label>ZIP</Label>
              <Input
                value={formData.zip}
                onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                placeholder="94105"
              />
            </div>
          </div>
        </div>
        
        {/* Actions - Sticky on mobile */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
          <Button 
            variant="outline"
            onClick={() => setCurrentStep(oauthStatus?.status === 'connected' ? STEPS.SEARCH : STEPS.CONNECT)}
            className="min-h-[44px] sm:min-h-[40px] w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 min-h-[48px] sm:min-h-[40px]"
            onClick={() => (signupInfo?.phone_verified ? handleSubmit() : setCurrentStep(STEPS.VERIFY_PHONE))}
            disabled={submitting || !formData.restaurant_name.trim()}
            data-testid="complete-onboarding-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : signupInfo?.phone_verified ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            {signupInfo?.phone_verified ? 'Create Restaurant' : 'Continue'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="container max-w-2xl mx-auto px-3 sm:px-4 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
            </div>
            <span className="text-lg sm:text-xl font-bold">Chain29</span>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold mb-2">Set Up Your Restaurant</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Let's get your restaurant ready for business
          </p>
        </div>
        
        {/* Step Indicator */}
        {renderStepIndicator()}
        
        {/* Current Step Content */}
        {currentStep === STEPS.VERIFY_PHONE && renderVerifyPhoneStep()}
        {currentStep === STEPS.SEARCH && renderSearchStep()}
        {currentStep === STEPS.CONNECT && renderConnectStep()}
        {currentStep === STEPS.CONFIRM && renderConfirmStep()}
      </div>
    </div>
  );
}
